import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { isPast, subMinutes } from 'date-fns'
import { lastValueFrom, Observable } from 'rxjs'
import { APIResponse } from '../../core/models/api-validation-result'
import { LOCAL_STORAGE } from '../../core/tokens/local-storage'
import { AuthenticationResult } from '../../core/types/authentication-result'
import { EnvUtilities } from '../../core/utilities/env-utilities'
import { AuthToken } from '../models/auth-token'
import { HttpMethod } from '../types/http-method'
import { PortalToken } from '../types/portal-token'
import type { Query } from '../types/query'
import { RequestParams } from '../types/request-params'
import { routes } from '../utilities/api-routes'
import { buildRequestParams } from '../utilities/params'
import { FractalService } from './fractal.service'

export type RequestHeaders = Record<string, string>

export enum ResponseType {
  json = 'json',
  file = 'file',
  raw = 'raw',
}

@Injectable({ providedIn: 'root' })
export class HttpService {
  private http = inject(HttpClient)
  private fractal = inject(FractalService)
  private localStorage = inject(LOCAL_STORAGE)

  private refreshing = false
  private onRefreshed: (() => Promise<void>)[] = []
  private static token: AuthToken | null = null

  async get<T>(
    path: string,
    parameters?: RequestParams,
    headers?: RequestHeaders,
    responseType: ResponseType = ResponseType.json
  ): Promise<Observable<T>> {
    const options = await this.constructOptions(
      path,
      parameters,
      headers,
      responseType
    )
    if (!options) {
      return Promise.reject()
    }
    return this.http.get<T>(path, options)
  }

  async post<T>(
    path: string,
    body?: unknown,
    parameters?: RequestParams,
    headers?: RequestHeaders,
    responseType: ResponseType = ResponseType.json
  ): Promise<Observable<T>> {
    const options = await this.constructOptions(
      path,
      parameters,
      headers,
      responseType
    )
    if (!options) {
      return Promise.reject(null)
    }
    return this.http.post<T>(path, body, options)
  }

  async put<T>(
    path: string,
    body?: unknown,
    parameters?: RequestParams,
    headers?: RequestHeaders,
    responseType: ResponseType = ResponseType.json
  ): Promise<Observable<T>> {
    const options = await this.constructOptions(
      path,
      parameters,
      headers,
      responseType
    )
    if (!options) {
      return Promise.reject()
    }
    return this.http.put<T>(path, body, options)
  }

  async patch<T>(
    path: string,
    body?: unknown,
    parameters?: RequestParams,
    headers?: RequestHeaders,
    responseType: ResponseType = ResponseType.json
  ): Promise<Observable<T>> {
    const options = await this.constructOptions(
      path,
      parameters,
      headers,
      responseType
    )
    if (!options) {
      return Promise.reject()
    }
    return this.http.patch<T>(path, body, options)
  }

  async delete<T>(
    path: string,
    parameters?: RequestParams,
    headers?: RequestHeaders,
    responseType: ResponseType = ResponseType.json
  ): Promise<Observable<T>> {
    const options = await this.constructOptions(
      path,
      parameters,
      headers,
      responseType
    )
    if (!options) {
      return Promise.reject()
    }
    return this.http.delete<T>(path, options)
  }

  setUserToken(token: AuthToken | null) {
    this.localStorage.removeItem('token')
    this.localStorage.removeItem('refresh')
    this.localStorage.removeItem('expires')
    HttpService.token = token
  }

  hasPortalToken(): boolean {
    return !!this.localStorage.getItem('portalToken')
  }

  setPortalToken(token: PortalToken | null) {
    this.localStorage.setItem('portalToken', token ? token.token : '')
    this.localStorage.setItem(
      'portalEngineerID',
      token ? `${token.engineerID}` : ''
    )
  }

  private async constructOptions(
    path: string,
    parameters: RequestParams | undefined,
    requestHeaders: RequestHeaders | undefined,
    responseType: ResponseType
  ) {
    let headers: RequestHeaders = {
      'x-manager-version': EnvUtilities.rawVersion(),
    }
    if (requestHeaders != null) {
      for (const key in requestHeaders) {
        if (requestHeaders[key]) {
          const value = requestHeaders[key].toString()
          if (value.length > 0) {
            headers[key] = value
          }
        }
      }
    }
    if (requestHeaders != null && !requestHeaders['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }
    const isPortal = path.startsWith(EnvUtilities.serviceUrl('', 'engineer'))
    if (isPortal) {
      const token = this.localStorage.getItem('portalToken')
      const engineer = this.localStorage.getItem('portalEngineerID')
      if (token && engineer) {
        headers['x-portal-token'] = token
        headers['x-portal-engineer'] = engineer
      }
    } else {
      const requiresToken = ![
        EnvUtilities.routeUrl(routes.api.management.auth.logIn),
        EnvUtilities.routeUrl(routes.api.management.auth.verify),
        EnvUtilities.routeUrl(routes.api.management.auth.refresh),
        EnvUtilities.routeUrl(routes.api.management.auth.forgotPassword),
        EnvUtilities.routeUrl(routes.api.management.auth.resetPassword),
        EnvUtilities.routeUrl(routes.api.site.v1.sites),
        EnvUtilities.routeUrl(routes.api.site.v1.attendees),
      ].includes(path)
      if (requiresToken) {
        const configuredHeaders = await this.configureRequest(headers)
        if (!configuredHeaders) {
          return null
        }
        headers = configuredHeaders
      }
    }
    let params = new HttpParams()
    if (parameters) {
      params = buildRequestParams(params, parameters, '')
    }
    return {
      headers: new HttpHeaders(headers),
      params,
      ...(responseType === ResponseType.file
        ? { observe: 'response', responseType: 'blob' }
        : responseType === ResponseType.raw
          ? { observe: 'response' }
          : {}),
      withCredentials: true,
    } as object
  }

  private async configureRequest(headers: RequestHeaders) {
    return new Promise<RequestHeaders | null>((resolve) => {
      const done = async (cancel: boolean) => {
        if (cancel) {
          resolve(null)
        }
        let failed = false
        if (this.refreshing) {
          this.refreshing = false
          for (const onRefreshed of this.onRefreshed) {
            if (
              !(await onRefreshed()
                .then(() => true)
                .catch(() => false))
            ) {
              failed = true
            }
          }
          this.onRefreshed = []
        }
        if (failed) {
          resolve(null)
        } else {
          if (HttpService.token?.token) {
            headers['Authorization'] = `Bearer ${HttpService.token?.token}`
          }
          resolve(headers)
        }
      }
      if (
        !HttpService.token ||
        isPast(subMinutes(HttpService.token.expiresAt, 2))
      ) {
        if (!this.refreshing) {
          this.refreshing = true
          const legacyToken = this.localStorage.getItem('refresh')
          this.post<APIResponse<AuthenticationResult>>(
            EnvUtilities.routeUrl(routes.api.management.auth.refresh),
            legacyToken ? { token: legacyToken } : undefined
          )
            .then((observable) => lastValueFrom(observable))
            .then((response) => {
              const token = AuthToken.fromResult(response.data)
              if (token) {
                this.setUserToken(token)
              }
              done(false)
            })
            .catch(() => {
              this.setUserToken(null)
              done(true)
            })
        } else {
          this.onRefreshed.push(
            () =>
              new Promise((resolve) => {
                done(false)
                  .then(() => resolve())
                  .catch(() => resolve())
              })
          )
        }
      } else {
        done(false).then(() => null)
      }
    })
  }

  crudRequest<T>(
    url: string,
    data?: unknown,
    duplicate = false,
    query?: Query
  ): Promise<Observable<T>> {
    let method: HttpMethod = 'post'
    if (data && (data as Record<string, unknown>)['id']) {
      url = `${url}/${(data as Record<string, unknown>)['id']}`
      if (duplicate) {
        ;(data as Record<string, unknown>)['id'] = 0
      } else {
        method = 'put'
      }
    }
    return this[method](EnvUtilities.serviceUrl(url, 'manage', query), data)
  }

  fractalList<Response, Model>(
    response: unknown,
    map: (data: Response) => Model
  ): Model[] {
    return this.fractal.deserialiseList<Response, Model>(response, map)
  }

  fractalItem<Response, Model>(
    response: unknown,
    map: (data: Response) => Model
  ): Model {
    return this.fractal.deserialiseItem<Response, Model>(response, map)
  }
}
